import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { SPACE_COLORS } from "@/lib/constants";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";

const PROJECT_ICONS = ["📁", "🏗️", "✈️", "💍", "🚀", "🏠", "📣", "🎯", "🛒", "🎓", "🧹", "⚙️"];

export const CreateProjectDialog = ({ workspaceId, open, onOpenChange }) => {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [icon, setIcon] = useState("📁");
  const [color, setColor] = useState(SPACE_COLORS[0]);
  const [saving, setSaving] = useState(false);

  const create = async () => {
    if (!name.trim()) return toast.error("El nombre es obligatorio");
    setSaving(true);
    try {
      const { data } = await api.post(`/workspaces/${workspaceId}/projects`, { name, description, icon, color });
      qc.invalidateQueries({ queryKey: ["projects", workspaceId] });
      toast.success(`Proyecto "${data.name}" creado`);
      onOpenChange(false);
      setName(""); setDescription("");
      navigate(`/space/${workspaceId}/project/${data.id}`);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="create-project-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl">Nuevo Proyecto</DialogTitle>
          <DialogDescription>Un Proyecto agrupa tareas con un mismo objetivo.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label>Nombre</Label>
            <Input data-testid="project-name-input" value={name} onChange={(e) => setName(e.target.value)}
              placeholder="Ej: Reforma cocina" maxLength={100} />
          </div>
          <div className="space-y-2">
            <Label>Descripción (opcional)</Label>
            <Textarea data-testid="project-description-input" value={description}
              onChange={(e) => setDescription(e.target.value)} rows={2} maxLength={500} />
          </div>
          <div className="space-y-2">
            <Label>Icono</Label>
            <div className="flex flex-wrap gap-1.5">
              {PROJECT_ICONS.map((ic) => (
                <button key={ic} data-testid={`project-icon-${ic}`} onClick={() => setIcon(ic)}
                  className={`h-9 w-9 rounded-lg text-lg flex items-center justify-center border transition-colors ${
                    icon === ic ? "border-primary bg-accent" : "border-transparent hover:bg-secondary"
                  }`}>{ic}</button>
              ))}
            </div>
          </div>
          <div className="space-y-2">
            <Label>Color</Label>
            <div className="flex flex-wrap gap-2">
              {SPACE_COLORS.map((c) => (
                <button key={c} data-testid={`project-color-${c.replace("#", "")}`} onClick={() => setColor(c)}
                  className={`h-7 w-7 rounded-full transition-transform ${color === c ? "ring-2 ring-offset-2 ring-primary scale-110" : ""}`}
                  style={{ backgroundColor: c }} />
              ))}
            </div>
          </div>
          <Button className="w-full" data-testid="project-create-submit-btn" onClick={create} disabled={saving}>
            {saving ? "Creando..." : "Crear Proyecto"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
